// src/components/UI.jsx — SwimDesk design system components

export function Card({ children, style, padding }) {
  return (
    <div style={{
      background: 'var(--card-bg)',
      border: '1px solid var(--border)',
      borderRadius: 'var(--radius-lg)',
      boxShadow: 'var(--shadow)',
      overflow: 'hidden',
      padding: padding || 0,
      ...style
    }}>
      {children}
    </div>
  );
}

export function SectionTitle({ children, actions, style }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      padding: '12px 18px',
      borderBottom: '1px solid var(--border)',
      ...style
    }}>
      <span style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--fg3)', fontFamily: 'var(--font)' }}>{children}</span>
      {actions && <div style={{ display: 'flex', gap: 6 }}>{actions}</div>}
    </div>
  );
}

export function Field({ label, children, hint, style }) {
  return (
    <div style={{ marginBottom: 14, ...style }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: 'var(--fg2)', marginBottom: 6, letterSpacing: '0.01em' }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

export function Input({ style, ...props }) {
  return (
    <input
      style={{
        width: '100%', padding: '8px 11px',
        border: '1px solid var(--input-border)',
        borderRadius: 'var(--radius)',
        background: 'var(--input-bg)', color: 'var(--fg)',
        fontSize: 13, fontFamily: 'var(--font)',
        outline: 'none',
        ...style
      }}
      onFocus={e => e.target.style.borderColor = 'var(--accent)'}
      onBlur={e => e.target.style.borderColor = 'var(--input-border)'}
      {...props}
    />
  );
}

export function Select({ children, style, ...props }) {
  return (
    <select
      style={{
        width: '100%', padding: '8px 11px',
        border: '1px solid var(--input-border)',
        borderRadius: 'var(--radius)',
        background: 'var(--input-bg)', color: 'var(--fg)',
        fontSize: 13, fontFamily: 'var(--font)', outline: 'none', cursor: 'pointer',
        ...style
      }}
      {...props}
    >
      {children}
    </select>
  );
}

export function Btn({ variant = 'primary', children, style, ...props }) {
  const base = { padding: '7px 14px', fontSize: 13, fontWeight: 500, cursor: 'pointer', fontFamily: 'var(--font)', borderRadius: 'var(--radius)', border: 'none', display: 'inline-flex', alignItems: 'center', gap: 6, whiteSpace: 'nowrap' };
  const variants = {
    primary: { background: 'var(--accent)', color: '#fff', boxShadow: '0 1px 2px rgba(37,99,235,0.3)' },
    danger:  { background: 'var(--danger)', color: '#fff' },
    outline: { background: 'transparent', color: 'var(--accent)', border: '1px solid var(--accent)', boxShadow: 'none' },
    ghost:   { background: 'var(--bg3)', color: 'var(--fg2)', border: '1px solid var(--border)', boxShadow: 'none' },
    success: { background: 'var(--success)', color: '#fff' },
  };
  return (
    <button
      style={{ ...base, ...variants[variant], ...(props.disabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}), ...style }}
      onMouseEnter={e => { if (!props.disabled) e.currentTarget.style.opacity = '0.88'; }}
      onMouseLeave={e => { e.currentTarget.style.opacity = '1'; }}
      {...props}
    >
      {children}
    </button>
  );
}

export function Badge({ variant = 'gray', children, style }) {
  const variants = {
    green:  { background: 'var(--success-dim)', color: 'var(--success)' },
    amber:  { background: 'var(--warning-dim)', color: 'var(--warning)' },
    blue:   { background: 'var(--accent-dim)',  color: 'var(--accent)' },
    red:    { background: 'var(--danger-dim)',  color: 'var(--danger)' },
    purple: { background: '#f3e8ff', color: '#7c3aed' },
    gray:   { background: 'var(--bg3)', color: 'var(--fg3)' },
  };
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', fontSize: 11, fontWeight: 600, borderRadius: 20, fontFamily: 'var(--font)', ...variants[variant], ...style }}>
      {children}
    </span>
  );
}

export function Flash({ type = 'info', children, onDismiss }) {
  const styles = {
    info:    { borderColor: 'var(--accent)',   background: 'var(--accent-dim)',  color: 'var(--accent-text)' },
    error:   { borderColor: 'var(--danger)',   background: 'var(--danger-dim)',  color: 'var(--danger)' },
    success: { borderColor: 'var(--success)',  background: 'var(--success-dim)', color: 'var(--success)' },
    warning: { borderColor: 'var(--warning)',  background: 'var(--warning-dim)', color: 'var(--warning)' },
  };
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', padding: '10px 14px', borderLeft: '3px solid', borderRadius: 'var(--radius)', fontSize: 13, fontWeight: 500, marginBottom: 14, ...styles[type] }}>
      <span>{children}</span>
      {onDismiss && <button onClick={onDismiss} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', marginLeft: 10, opacity: 0.6, fontSize: 16, lineHeight: 1 }}>×</button>}
    </div>
  );
}

export function Modal({ title, onClose, children, width = 560 }) {
  return (
    <div
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(2px)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{ width: '100%', maxWidth: width, background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', animation: 'modalIn 0.15s ease' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 18px', borderBottom: '1px solid var(--border)' }}>
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--fg)' }}>{title}</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--fg3)', fontSize: 18, lineHeight: 1, padding: '2px 6px', borderRadius: 4 }}>×</button>
        </div>
        <div style={{ padding: 18, overflowY: 'auto' }}>{children}</div>
      </div>
    </div>
  );
}

export function Table({ headers, children, style }) {
  return (
    <div style={{ overflowX: 'auto', ...style }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
        <thead>
          <tr>
            {headers.map((h, i) => (
              <th key={i} style={{ padding: '9px 14px', textAlign: 'left', fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg3)', borderBottom: '1px solid var(--border)', background: 'var(--bg)', whiteSpace: 'nowrap' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function TR({ children, onClick, highlight }) {
  return (
    <tr
      onClick={onClick}
      style={{ borderBottom: '1px solid var(--border-light)', background: highlight ? 'var(--warning-dim)' : undefined, cursor: onClick ? 'pointer' : undefined }}
      onMouseEnter={e => onClick && (e.currentTarget.style.background = 'var(--bg3)')}
      onMouseLeave={e => onClick && (e.currentTarget.style.background = highlight ? 'var(--warning-dim)' : '')}
    >
      {children}
    </tr>
  );
}

export function TD({ children, style }) {
  return <td style={{ padding: '9px 14px', color: 'var(--fg2)', verticalAlign: 'middle', ...style }}>{children}</td>;
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 22, flexWrap: 'wrap', gap: 12 }}>
      <div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: 'var(--fg)', letterSpacing: '-0.02em' }}>{title}</h1>
        {subtitle && <p style={{ fontSize: 13, color: 'var(--fg3)', marginTop: 3 }}>{subtitle}</p>}
      </div>
      {actions && <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>{actions}</div>}
    </div>
  );
}

export function Spinner() {
  return (
    <div style={{ padding: 48, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: 24, height: 24, border: '2px solid var(--border)', borderTopColor: 'var(--accent)', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
    </div>
  );
}

export function Empty({ message }) {
  return <div style={{ padding: '40px 20px', textAlign: 'center', color: 'var(--fg3)', fontSize: 13 }}>{message}</div>;
}

export function StatCard({ label, value, color, sub }) {
  return (
    <div style={{ background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '16px 18px', boxShadow: 'var(--shadow)', flex: 1, minWidth: 120 }}>
      <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--fg3)', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: color || 'var(--fg)', letterSpacing: '-0.02em', lineHeight: 1 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: 'var(--fg3)', marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

// Global animation keyframes injected once
if (!document.getElementById('sd-keyframes')) {
  const s = document.createElement('style');
  s.id = 'sd-keyframes';
  s.textContent = `
    @keyframes spin { to { transform: rotate(360deg); } }
    @keyframes modalIn { from { opacity: 0; transform: translateY(-8px) scale(0.98); } to { opacity: 1; transform: none; } }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    @keyframes slideUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: none; } }
  `;
  document.head.appendChild(s);
}
